import React, { useEffect, useRef, useState } from 'react';
import { Editor, type EditorState, ActionMethodsWithConfig } from '@craftjs/core';
import { RenderNode } from '@/components/userNodeRendering/RenderNode';
import { selectPreviewModeEnabled } from '@/store/system/selectors';
import type { Delete, PatchListenerAction } from '@craftjs/utils';
import { ZoomAnchoredHandlers } from './CustomEventHandler';
import { selectPanZoomState } from '@/store/zoom/selectors';
import type { PanZoomState } from '@/store/zoom/panZoomSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchFeatureFlags } from '@/store/featureFlags/thunks';
import { EditorLayout } from './EditorLayout';
import { resolver } from '@/utils/helperFunc';
import { SaveSection } from '@/components/panels/sections/SaveSection/SaveSection';
import { DropTargetIndicator } from '@/components/canvas/DropTargetIndicator/DropTargetIndicator';
import { useMixpanel } from '@/hooks/useMixpanel';
import { trackEditorEvent } from '@/utils/editorTracking';
import { selectConsecutiveSaveFailures } from '@/store/pages/selectors';
import { useNetworkError } from '@/context';

interface EditorProps {
  className?: string;
  disabled?: boolean;
}

export type moveEndFuncType = (
  newState: EditorState,
  prevState: EditorState,
  actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
) => void;

export type saveJsonCbType = () => void;

export const Builder: React.FC<EditorProps> = ({ className, disabled = false }) => {
  const dispatch = useAppDispatch();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const retryTimer = useRef<NodeJS.Timeout | null>(null);
  const [frameRef, setFrameRef] = useState<HTMLDivElement | null>(null);

  const previewMode = useAppSelector(selectPreviewModeEnabled);
  const state = useAppSelector(selectPanZoomState);
  const consecutiveSaveFailures = useAppSelector(selectConsecutiveSaveFailures);
  const moveEndFunc = useRef<moveEndFuncType>(() => {});
  const panZoomStateRef = useRef<PanZoomState>(state);
  const saveJsonCbRef = useRef<saveJsonCbType>(() => {});
  const { hasNetworkError } = useNetworkError();
  const track = useMixpanel();

  const allowEditing = !previewMode && !disabled && !hasNetworkError;

  useEffect(() => {
    void dispatch(fetchFeatureFlags());
  }, [dispatch]);

  useEffect(() => {
    panZoomStateRef.current = state;
  }, [state]);

  // Retry save after 30 seconds on failure (only for first 2 failures)
  // Only works when the browser is online
  // Won't retry if there is a network error
  useEffect(() => {
    // Clear any existing retry timer first
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }

    // Only schedule retry if online and has failures
    if (!hasNetworkError && consecutiveSaveFailures > 0 && consecutiveSaveFailures < 3) {
      retryTimer.current = setTimeout(() => {
        saveJsonCbRef.current();
      }, 30000);
    }

    return () => {
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    };
  }, [consecutiveSaveFailures, hasNetworkError]);

  const registerNormalizeNode = (normalizeNodeHandler: moveEndFuncType) => {
    moveEndFunc.current = normalizeNodeHandler;
  };

  const registerSaveJsonCb = (saveJsonCb: saveJsonCbType) => {
    saveJsonCbRef.current = saveJsonCb;
  };

  const handleMoveEnd = (
    newState: EditorState,
    prevState: EditorState,
    actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
  ) => {
    if (moveEndFunc.current) {
      moveEndFunc.current(newState, prevState, actionPerformed);
    }
    trackEditorEvent(actionPerformed, newState, prevState, { track });
  };

  // On any node changes, we debounce a save to craft json
  const handleNodesChange = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      saveJsonCbRef.current();
    }, 800);
  };

  return (
    <Editor
      onNodesChange={allowEditing ? handleNodesChange : undefined}
      handlers={(store) =>
        new ZoomAnchoredHandlers({
          store,
          isMultiSelectEnabled: () => false,
          removeHoverOnMouseleave: true,
          getZoom: () => panZoomStateRef,
        })
      }
      resolver={resolver}
      indicator={{
        success: 'transparent',
        error: 'transparent',
        transition: 'none',
      }}
      normalizeNodes={handleMoveEnd}
      enabled={allowEditing}
      onRender={RenderNode}
    >
      <EditorLayout
        className={className}
        frameRef={frameRef}
        onRegisterSaveJson={registerSaveJsonCb}
        onNormalizeNode={registerNormalizeNode}
        onFrameRef={setFrameRef}
        disabled={disabled}
      />
      <DropTargetIndicator />
      {allowEditing && <SaveSection />}
    </Editor>
  );
};
